import React from 'react';
import Dialog, { DialogClasses } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ReactComponent as CloseIcon } from 'src/assets/icons/modal-close-btn.svg';
import ButtonV2 from '../ButtonV2';
import { IconButton } from '../IconButton';
import Tooltip, { ITooltipPlacement } from '../Tooltip';

type ModalProps = {
  open: boolean;
  type: 'dialog' | 'modal';
  header: string | React.ReactNode;
  subheader?: string | React.ReactNode;
  children?: React.ReactNode;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  closeActionConfig?: ActionConfig;
  submitActionConfig?: ActionConfig;
  customActionConfig?: ActionConfig;
  middleActionConfig?: ActionConfig;
  contentDivider?: boolean;
  onClose: () => void;
  disableCloseIcon?: boolean;
  renderCloseIcon?: boolean;
  classes?: Partial<DialogClasses>;
  hideDialogAction?: boolean;
  hideHeader?: boolean;
  showMiddleButton?: boolean;
  changeFooterBg?: boolean;
  disableEnforceFocus?: boolean;
  customActionElement?: React.ReactNode;
  PaperProps?: object;
};

export type ActionConfig = {
  handleAction: (value: any) => void;
  label: string;
  disabled?: boolean;
  icon?: any;
  loading?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  id: string;
  tooltipData?: {
    title: string;
    placement: ITooltipPlacement;
    theme: any;
    arrow: boolean;
  };
};

function ModalV2({
  open,
  header,
  type,
  subheader,
  fullWidth,
  children,
  maxWidth,
  closeActionConfig,
  submitActionConfig,
  contentDivider = false,
  onClose,
  disableCloseIcon = false,
  customActionConfig,
  renderCloseIcon = true,
  classes,
  hideDialogAction,
  hideHeader = false,
  middleActionConfig,
  showMiddleButton = false,
  changeFooterBg = false,
  disableEnforceFocus = false,
  customActionElement,
  PaperProps,
}: ModalProps) {
  return (
    <>
      <Dialog
        classes={classes}
        open={open}
        onClose={onClose}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        className="mui-modal-container-main"
        disableEnforceFocus={disableEnforceFocus}
        PaperProps={PaperProps}
      >
        {!hideHeader && (
          <DialogTitle
            className={`mui-modal-header ${
              type === 'dialog' ? 'mui-modal-header-dialog' : 'mui-modal-header-modal'
            }`}
          >
            <div className="mui-modal-header-text">
              <div className="mui-modal-title">
                {React.isValidElement(header) ? (
                  header
                ) : (
                  <h3 className="mui-modal-title-text">{header}</h3>
                )}
              </div>
              {subheader && <div className="mui-modal-subtitle">{subheader}</div>}
            </div>
            {renderCloseIcon && (
              <IconButton aria-label="close" onClick={onClose} disabled={disableCloseIcon}>
                <CloseIcon />
              </IconButton>
            )}
          </DialogTitle>
        )}
        {children && (
          <DialogContent
            dividers={contentDivider}
            className={`mui-modal-content ${contentDivider && 'mui-modal-content-divider'}`}
          >
            {children}
          </DialogContent>
        )}
        {!hideDialogAction && (
          <DialogActions
            className={`mui-modal-actions ${(customActionConfig || customActionElement) && 'mui-modal-actions-justified'} ${
              changeFooterBg ? 'changeed-bg' : ''
            }`}
          >
            {(customActionConfig || customActionElement) && (
              <div className="mui-modal-actions-left">
                {customActionConfig && (
                  <ButtonV2
                    type="inherited"
                    startIcon={customActionConfig.icon}
                    text={customActionConfig.label}
                    variant={customActionConfig.variant || 'text'}
                    onClick={customActionConfig.handleAction}
                    disabled={customActionConfig.disabled}
                    loading={customActionConfig.loading}
                    id={customActionConfig.id}
                  />
                )}
                {customActionElement && customActionElement}
              </div>
            )}
            <div className="modal-actions-container-action-buttons">
              {closeActionConfig && (
                <ButtonV2
                  type="inherited"
                  variant={closeActionConfig.variant || 'text'}
                  onClick={closeActionConfig.handleAction}
                  startIcon={closeActionConfig.icon}
                  text={closeActionConfig.label}
                  loading={closeActionConfig.loading}
                  disabled={closeActionConfig.disabled}
                  id={closeActionConfig.id}
                />
              )}
              {showMiddleButton && middleActionConfig && (
                <ButtonV2
                  type="primary"
                  startIcon={middleActionConfig.icon}
                  text={middleActionConfig.label}
                  variant={middleActionConfig.variant || 'text'}
                  onClick={middleActionConfig.handleAction}
                  disabled={middleActionConfig.disabled}
                  loading={middleActionConfig.loading}
                  id={middleActionConfig.id}
                />
              )}
              {submitActionConfig && (
                <Tooltip
                  title={submitActionConfig?.tooltipData?.title}
                  istheme={submitActionConfig?.tooltipData?.theme}
                  arrow={submitActionConfig?.tooltipData?.arrow}
                  placement={submitActionConfig?.tooltipData?.placement || 'top'}
                  hide={submitActionConfig?.tooltipData === undefined}
                >
                  <div>
                    <ButtonV2
                      type="primary"
                      variant={submitActionConfig.variant || 'contained'}
                      onClick={submitActionConfig.handleAction}
                      startIcon={submitActionConfig.icon}
                      text={submitActionConfig.label}
                      loading={submitActionConfig.loading}
                      disabled={submitActionConfig.disabled}
                      id={submitActionConfig.id}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
}

export default ModalV2;
